import { api } from '@/lib/api/api'
import { toast } from 'react-toastify'
import {
  ApiExecutionData,
  ApiWorkflow,
  ApiWorkflowMetadata,
  WorkflowRefStrict,
} from 'sprites-types/api/v2/workflows'
import { WorkflowConfig } from 'sprites-types/workflow/core'

export interface WorkflowResponse {
  success: boolean
  workflow: ApiWorkflow
}

export interface WorkflowMetadataResponse {
  success: boolean
  metadata: ApiWorkflowMetadata
}

export interface WorkspaceWorkflowMetadataResponse {
  success: boolean
  workflows: ApiWorkflowMetadata[]
  pagination?: WorkflowPagination
}

export interface WorkflowPagination {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export interface WorkflowsResponse {
  workflows: ApiWorkflowMetadata[]
  pagination: WorkflowPagination
}

export interface GetWorkflowsParams {
  limit?: number
  offset?: number
  bookmarked?: boolean
}

export interface UpdateWorkflowRequest {
  workflowRef: WorkflowRefStrict
  config: Partial<WorkflowConfig>
  sessionId?: string // Optional session ID to update session context
}

export interface RerunFromNodeRequest {
  executionId: string
  nodeExecutionId: string
  workflowRef: WorkflowRefStrict
  resumeStage?: 'tool_execution' | 'edge_selection'
}

export interface RerunFromNodeResponse {
  success: boolean
  executionId: string
  status: string
  message: string
  variables: any
  nodeExecutions: any[]
  createdAt: string
  updatedAt: string
}

export interface Tag {
  id: string
  name: string
  workspaceId: string | null
}

export interface GetWorkspaceTagsResponse {
  success: boolean
  tags: Tag[]
}

export interface ExecutionsPagination {
  total: number
  limit: number
  offset: number
  hasMore: boolean
}

export interface GetExecutionsParams {
  limit?: number
  offset?: number
}

export interface ExecutionsResponse {
  executions: ApiExecutionData[]
  pagination: ExecutionsPagination
}

export interface WorkspaceExecutionsResponse {
  success: boolean
  executions: ApiExecutionData[]
  pagination?: ExecutionsPagination
}

export const workflowApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getWorkflow: builder.query<ApiWorkflow, string>({
      query: (workflowId) => {
        console.log('[WorkflowAPI] Making workflow query', {
          workflowId,
          url: `v2/workflows/${workflowId}`,
          timestamp: new Date().toISOString(),
        })
        return {
          url: `v2/workflows/${workflowId}`,
          credentials: 'include',
        }
      },
      transformResponse: (response: WorkflowResponse): ApiWorkflow => {
        if (!response.success) {
          throw new Error('Failed to fetch workflow')
        }

        return response.workflow
      },
      async onQueryStarted(workflowId, { dispatch, queryFulfilled }) {
        try {
          const { data: workflow } = await queryFulfilled
          // Only update metadata cache if workflow has metadata property
          if (workflow.metadata?.id) {
            dispatch(
              workflowApi.util.upsertQueryData(
                'getWorkflowMetadata',
                workflow.metadata.id,
                workflow.metadata
              )
            )
          }
        } catch (error: any) {
          // Handle 404 errors specifically - workflow was deleted
          if (error?.status === 404 || error?.data?.status === 404) {
            console.warn(`[WorkflowAPI] Workflow ${workflowId} not found (404) - likely deleted`)

            // Clear workflow context - we'll use a global event to notify the CopilotContext
            // This approach ensures the context clearing works regardless of component hierarchy
            const workflowDeletedEvent = new CustomEvent('workflowDeleted', {
              detail: { workflowId },
            })
            if (typeof window !== 'undefined') {
              window.dispatchEvent(workflowDeletedEvent)
            }

            return
          }

          // Silently handle other errors in cache updates to avoid unhandled promise rejections
          console.warn('Failed to update workflow metadata cache:', error)
        }
      },
      providesTags: (result, _error, workflowId) => [
        { type: 'Workflow' as const, id: workflowId },
        { type: 'WorkflowMetadata' as const, id: 'LIST' },
        { type: 'WorkflowMetadata' as const, id: workflowId },
      ],
    }),
    getWorkflowMetadata: builder.query<ApiWorkflowMetadata, string>({
      queryFn: async (workflowId, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({
          url: `v2/workflows/${workflowId}/metadata`,
          credentials: 'include',
        })

        if (result.error) {
          // Handle 404 errors specifically for workflow metadata
          if (result.error.status === 404) {
            console.warn(
              `[WorkflowAPI] Workflow metadata ${workflowId} not found (404) - likely deleted`
            )

            // Show toast notification
            toast.error('The workflow version you were viewing has been deleted.')

            // Clear workflow context using global event
            const workflowDeletedEvent = new CustomEvent('workflowDeleted', {
              detail: { workflowId },
            })
            if (typeof window !== 'undefined') {
              window.dispatchEvent(workflowDeletedEvent)
            }
          }

          return { error: result.error }
        }

        const response = result.data as WorkflowMetadataResponse
        if (!response.success) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              data: { message: 'Failed to fetch workflow metadata' },
              error: 'Failed to fetch workflow metadata',
            },
          }
        }

        return { data: response.metadata }
      },
      providesTags: (result, _error, workflowId) => [
        { type: 'WorkflowMetadata' as const, id: workflowId },
        { type: 'WorkflowMetadata' as const, id: 'LIST' },
      ],
    }),
    getWorkspaceWorkflowMetadata: builder.query<WorkflowsResponse, GetWorkflowsParams>({
      queryFn: async (params: GetWorkflowsParams = {}, _queryApi, _extraOptions, fetchWithBQ) => {
        const { limit = 20, offset = 0, bookmarked } = params
        const searchParams = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        })

        if (bookmarked !== undefined) {
          searchParams.append('bookmarked', bookmarked.toString())
        }

        const result = await fetchWithBQ({
          url: `v1/users/me/workflows?${searchParams}`,
          credentials: 'include',
        })

        if (result.error) {
          return { error: result.error }
        }

        const response = result.data as WorkspaceWorkflowMetadataResponse
        if (!response.success) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              data: { message: 'Failed to fetch workspace workflow metadata' },
              error: 'Failed to fetch workspace workflow metadata',
            },
          }
        }

        return {
          data: {
            workflows: response.workflows,
            pagination: response.pagination ?? {
              total: response.workflows.length,
              limit,
              offset,
              hasMore: false,
            },
          },
        }
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          // Populate individual workflow metadata caches
          data.workflows.forEach((workflow) => {
            dispatch(workflowApi.util.upsertQueryData('getWorkflowMetadata', workflow.id, workflow))
          })
        } catch (error) {
          // Silently handle errors in cache updates to avoid unhandled promise rejections
          console.warn('Failed to update workflow metadata caches:', error)
        }
      },
      providesTags: (result) => [
        { type: 'WorkflowMetadata' as const, id: 'LIST' },
        ...(result?.workflows?.map((workflow) => ({
          type: 'WorkflowMetadata' as const,
          id: workflow.id,
        })) ?? []),
      ],
    }),
    updateWorkflow: builder.mutation<ApiWorkflow, UpdateWorkflowRequest>({
      query: ({ workflowRef, config, sessionId }) => {
        return {
          url: `v2/workflows/${workflowRef}`,
          method: 'PUT',
          credentials: 'include',
          body: { config, sessionId },
        }
      },
      transformResponse: (response: WorkflowResponse): ApiWorkflow => {
        if (!response.success) {
          throw new Error('Failed to update workflow')
        }
        return response.workflow
      },
      async onQueryStarted({ workflowRef, config }, { dispatch, queryFulfilled }) {
        const patches: Array<{ undo: () => void }> = []

        // Optimistically update getWorkflow cache
        try {
          const patch = dispatch(
            workflowApi.util.updateQueryData('getWorkflow', workflowRef, (draft) => {
              if (draft?.metadata) {
                // Update metadata with new config values
                if (config.name !== undefined) draft.metadata.name = config.name
                if (config.description !== undefined)
                  draft.metadata.description = config.description
              }
              if (draft?.data) {
                // Update data config with new values
                Object.assign(draft.data, config)
              }
            })
          )
          patches.push(patch)
        } catch (e) {
          console.debug('Error updating getWorkflow cache:', e)
        }

        // Optimistically update getWorkflowMetadata cache
        try {
          const patch = dispatch(
            workflowApi.util.updateQueryData('getWorkflowMetadata', workflowRef, (draft) => {
              if (draft) {
                if (config.name !== undefined) draft.name = config.name
                if (config.description !== undefined) draft.description = config.description
              }
            })
          )
          patches.push(patch)
        } catch (e) {
          console.debug('Error updating getWorkflowMetadata cache:', e)
        }

        try {
          await queryFulfilled
        } catch (error) {
          console.debug('Error updating getWorkflowMetadata cache:', error)
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (result, _error, { workflowRef }) => [
        { type: 'Workflow' as const, id: workflowRef },
        { type: 'WorkflowMetadata' as const, id: workflowRef },
        { type: 'WorkflowMetadata' as const, id: 'LIST' },
      ],
    }),
    deleteWorkflow: builder.mutation<
      { success: boolean; message: string; workflowId: string },
      string
    >({
      query: (workflowId: string) => ({
        url: `v2/workflows/${workflowId}`,
        method: 'DELETE',
        credentials: 'include',
      }),
      invalidatesTags: (result, _error, workflowId) => [
        { type: 'Workflow' as const, id: workflowId },
        { type: 'WorkflowMetadata' as const, id: workflowId },
        { type: 'WorkflowMetadata' as const, id: 'LIST' },
      ],
      extraOptions: { maxRetries: 1 },
    }),
    rerunFromNode: builder.mutation<RerunFromNodeResponse, RerunFromNodeRequest>({
      query: ({ executionId, nodeExecutionId, workflowRef, resumeStage = 'tool_execution' }) => ({
        url: `v2/workflows/execution/${executionId}/rerun/${nodeExecutionId}`,
        method: 'POST',
        credentials: 'include',
        body: { workflowRef, resumeStage },
      }),
      transformResponse: (response: RerunFromNodeResponse): RerunFromNodeResponse => {
        if (!response.success) {
          throw new Error('Failed to rerun from node')
        }
        return response
      },
    }),
    setWorkflowBookmark: builder.mutation<
      { success: boolean; isBookmarked: boolean; message: string },
      { workflowId: string; bookmarked: boolean; workflowRef?: string }
    >({
      query: ({ workflowId, bookmarked }) => ({
        url: `v2/workflows/${workflowId}/bookmark`,
        method: 'POST',
        credentials: 'include',
        body: { bookmarked },
      }),
      async onQueryStarted(
        { workflowId, bookmarked, workflowRef },
        { dispatch, queryFulfilled, getState }
      ) {
        const patches: Array<{ undo: () => void }> = []

        // Update all matching workflow cache entries
        // We need to check both workflowId and workflowRef formats
        const state = getState() as any
        const workflowCache = state.api?.queries

        if (workflowCache) {
          // Find all cache keys that match this workflow
          Object.keys(workflowCache).forEach((key) => {
            // Match getWorkflow queries with this workflowId or workflowRef
            if (
              key.startsWith('getWorkflow(') &&
              (key.includes(workflowId) || (workflowRef && key.includes(workflowRef)))
            ) {
              try {
                // Extract the cache key argument
                const match = key.match(/getWorkflow\("([^"]+)"\)/)
                if (match) {
                  const cacheKey = match[1]
                  const patch = dispatch(
                    workflowApi.util.updateQueryData('getWorkflow', cacheKey, (draft) => {
                      if (draft?.metadata) {
                        draft.metadata.isBookmarked = bookmarked
                      }
                    })
                  )
                  patches.push(patch)
                }
              } catch (e) {
                console.debug('Failed to update workflow cache entry:', key, e)
              }
            }

            // Match getWorkflowMetadata queries
            if (
              key.startsWith('getWorkflowMetadata(') &&
              (key.includes(workflowId) || (workflowRef && key.includes(workflowRef)))
            ) {
              try {
                const match = key.match(/getWorkflowMetadata\("([^"]+)"\)/)
                if (match) {
                  const cacheKey = match[1]
                  const patch = dispatch(
                    workflowApi.util.updateQueryData('getWorkflowMetadata', cacheKey, (draft) => {
                      if (draft) {
                        draft.isBookmarked = bookmarked
                      }
                    })
                  )
                  patches.push(patch)
                }
              } catch (e) {
                console.debug('Failed to update workflow metadata cache entry:', key, e)
              }
            }

            // Match getWorkspaceWorkflowMetadata queries
            if (key.startsWith('getWorkspaceWorkflowMetadata(')) {
              try {
                const match = key.match(/getWorkspaceWorkflowMetadata\(({[^}]+})\)/)
                if (match) {
                  const paramsStr = match[1]
                  const params = JSON.parse(paramsStr)
                  const patch = dispatch(
                    workflowApi.util.updateQueryData(
                      'getWorkspaceWorkflowMetadata',
                      params,
                      (draft) => {
                        const workflow = draft.workflows.find((w) => w.id === workflowId)
                        if (workflow) {
                          workflow.isBookmarked = bookmarked
                        }
                      }
                    )
                  )
                  patches.push(patch)
                }
              } catch (e) {
                console.debug('Failed to update workspace workflow metadata cache entry:', key, e)
              }
            }
          })
        }

        try {
          await queryFulfilled
        } catch {
          patches.forEach((patch) => patch.undo())
          toast.error('Failed to update bookmark')
        }
      },
      invalidatesTags: (_result, _error, { workflowId }) => [
        { type: 'Workflow' as const, id: workflowId },
        { type: 'WorkflowMetadata' as const, id: workflowId },
        { type: 'WorkflowMetadata' as const, id: 'LIST' },
      ],
    }),
    getWorkspaceTags: builder.query<Tag[], string | undefined>({
      query: (workspaceId) => {
        if (!workspaceId) {
          throw new Error('Workspace ID is required')
        }
        return {
          url: `v2/tags?workspaceId=${workspaceId}`,
          credentials: 'include',
        }
      },
      transformResponse: (response: GetWorkspaceTagsResponse): Tag[] => {
        if (!response.success) {
          throw new Error('Failed to fetch workspace tags')
        }
        return response.tags
      },
      providesTags: [{ type: 'Tag' as const, id: 'LIST' }],
    }),
    addWorkflowTag: builder.mutation<
      { success: boolean; message: string },
      { workflowId: string; tagId: string; tag: Tag }
    >({
      query: ({ workflowId, tagId }) => ({
        url: `v2/workflows/${workflowId}/tags`,
        method: 'POST',
        credentials: 'include',
        body: { tagId },
      }),
      async onQueryStarted({ workflowId, tag }, { dispatch, queryFulfilled }) {
        const patches: Array<{ undo: () => void }> = []

        // Optimistically update getWorkflow cache
        try {
          const patch = dispatch(
            workflowApi.util.updateQueryData('getWorkflow', workflowId, (draft) => {
              if (draft?.metadata) {
                draft.metadata.tags = draft.metadata.tags || []
                if (!draft.metadata.tags.find((t) => t.id === tag.id)) {
                  draft.metadata.tags.push(tag)
                }
              }
            })
          )
          patches.push(patch)
        } catch {
          // Cache entry might not exist
        }

        try {
          await queryFulfilled
        } catch {
          patches.forEach((patch) => patch.undo())
        }
      },
      invalidatesTags: (_result, _error, { workflowId }) => [
        { type: 'Workflow' as const, id: workflowId },
        { type: 'WorkflowMetadata' as const, id: workflowId },
        { type: 'WorkflowMetadata' as const, id: 'LIST' },
      ],
    }),
    removeWorkflowTag: builder.mutation<
      { success: boolean; message: string },
      { workflowId: string; tagId: string }
    >({
      query: ({ workflowId, tagId }) => ({
        url: `v2/workflows/${workflowId}/tags/${tagId}`,
        method: 'DELETE',
        credentials: 'include',
      }),
      invalidatesTags: (_result, _error, { workflowId }) => [
        { type: 'Workflow' as const, id: workflowId },
        { type: 'WorkflowMetadata' as const, id: workflowId },
        { type: 'WorkflowMetadata' as const, id: 'LIST' },
      ],
    }),
    createTag: builder.mutation<
      { success: boolean; tag: Tag },
      { name: string; workspaceId: string }
    >({
      query: ({ name, workspaceId }) => ({
        url: 'v2/tags',
        method: 'POST',
        credentials: 'include',
        body: { name, workspaceId },
      }),
      invalidatesTags: [{ type: 'Tag' as const, id: 'LIST' }],
    }),
    deleteTag: builder.mutation<{ success: boolean; message: string }, string>({
      query: (tagId) => ({
        url: `v2/tags/${tagId}`,
        method: 'DELETE',
        credentials: 'include',
      }),
      invalidatesTags: [{ type: 'Tag' as const, id: 'LIST' }],
    }),
    getWorkspaceExecutions: builder.query<ExecutionsResponse, GetExecutionsParams>({
      queryFn: async (params: GetExecutionsParams = {}, _queryApi, _extraOptions, fetchWithBQ) => {
        const { limit = 20, offset = 0 } = params
        const searchParams = new URLSearchParams({
          limit: limit.toString(),
          offset: offset.toString(),
        })

        const result = await fetchWithBQ({
          url: `v1/users/me/executions?${searchParams}`,
          credentials: 'include',
        })

        if (result.error) {
          return { error: result.error }
        }

        const response = result.data as WorkspaceExecutionsResponse
        if (!response.success) {
          return {
            error: {
              status: 'CUSTOM_ERROR' as const,
              data: { message: 'Failed to fetch workspace executions' },
              error: 'Failed to fetch workspace executions',
            },
          }
        }

        return {
          data: {
            executions: response.executions,
            pagination: response.pagination ?? {
              total: response.executions.length,
              limit,
              offset,
              hasMore: false,
            },
          },
        }
      },
      providesTags: [{ type: 'Execution' as const, id: 'LIST' }],
    }),
  }),
  overrideExisting: true,
})

export const {
  useGetWorkflowQuery,
  useGetWorkflowMetadataQuery,
  useGetWorkspaceWorkflowMetadataQuery,
  useUpdateWorkflowMutation,
  useDeleteWorkflowMutation,
  useRerunFromNodeMutation,
  useSetWorkflowBookmarkMutation,
  useGetWorkspaceTagsQuery,
  useAddWorkflowTagMutation,
  useRemoveWorkflowTagMutation,
  useCreateTagMutation,
  useDeleteTagMutation,
  useGetWorkspaceExecutionsQuery,
} = workflowApi
